'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { createColumnHelper } from '@tanstack/table-core';
import clsx from 'clsx';
import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BiCheckCircle, BiCircle } from 'react-icons/bi';
import { BsToggleOff, BsToggleOn } from 'react-icons/bs';
import { GrNodes } from 'react-icons/gr';
import { MdWarning } from 'react-icons/md';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useSecretUpdate, useSecretUpdateGlobal } from '@/hooks/mutations/useSecretMutations';
import { useByoaAgentsQuery } from '@/hooks/queries/useByoaAgents';
import { useSecretDetailQuery, useSecretsQuery } from '@/hooks/queries/useSecrets';
import { useAuthContext } from '@/hooks/useAuthContext';
import { SecretListItem } from '@/types/secret';

import styles from './manageAccessModal.module.scss';
import {
  charCounterText, generateEnvVarName, getExistingEnvVars, MAX_ENV_VAR_LENGTH
} from './secretHelpers';


// --- Constants ---

// Uppercase letters (A–Z), numbers (0–9), and underscores (_) only.
const ENV_VAR_REGEX = /^[A-Z0-9_]+$/;
const DEFAULT_PAGE_SIZE = 30;


// --- Schema ---

const envVarSchema = z.object( {
  envVarName: z.string()
    .min( 1, 'Variable name is required. Pick a new unique name.' )
    .max( MAX_ENV_VAR_LENGTH, `Maximum ${ MAX_ENV_VAR_LENGTH } characters` )
    .regex( ENV_VAR_REGEX, 'Must contain only uppercase letters, digits, or underscores.' )
} );

type EnvVarFormData = z.infer<typeof envVarSchema>;


// --- Types ---

type ConstellationRow = {
  id: string;
  name: string;
};

type ManageAccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  secret: SecretListItem;
};


// --- Component ---

const columnHelper = createColumnHelper<ConstellationRow>();

export function ManageAccessModal( { isOpen, onClose, secret }: ManageAccessModalProps ) {
  const [ globalEnabled, setGlobalEnabled ] = useState( secret.global );
  const [ selectedAgentIds, setSelectedAgentIds ] = useState<Set<string>>( () => new Set( ( secret.agentMappings ?? [] ).map( m => m.agentId ) )
  );
  const [ selectAllInWorkspace, setSelectAllInWorkspace ] = useState( false );
  const [ constellationFilter, setConstellationFilter ] = useState( '' );
  const [ serverError, setServerError ] = useState<string | null>( null );

  const { sessionContext } = useAuthContext();
  const userId = sessionContext?.userId ?? '';

  const { data: secretDetail } = useSecretDetailQuery( secret.id );
  const { data: agents } = useByoaAgentsQuery();
  const { data: secrets } = useSecretsQuery();

  const updateMutation = useSecretUpdate();
  const updateGlobalMutation = useSecretUpdateGlobal();

  const isPending = updateMutation.isPending || updateGlobalMutation.isPending;

  // Compute initial env var name
  const initialEnvVarName = useMemo( () => {
    if ( secret.globalEnvVarName ) return secret.globalEnvVarName;
    const mappings = secret.agentMappings ?? [];
    if ( mappings.length > 0 ) return mappings[0].envVarName;
    return generateEnvVarName( secret.name, getExistingEnvVars( secrets ?? [] ) );
  }, [ secret, secrets ] );

  const {
    register,
    watch,
    reset,
    formState: { errors, isValid }
  } = useForm<EnvVarFormData>( {
    resolver: zodResolver( envVarSchema ),
    defaultValues: { envVarName: initialEnvVarName },
    mode: 'onChange'
  } );

  const watchedEnvVar = watch( 'envVarName' );
  const envVarCharCounter = charCounterText( watchedEnvVar?.length ?? 0, MAX_ENV_VAR_LENGTH );

  // Reset state when the modal opens, or when a different secret is shown.
  // Depend on secret.id (not the whole secret object) so background secrets-list
  // refetches that hand back a new reference don't clobber in-progress edits.
  useEffect( () => {
    if ( isOpen ) {
      setGlobalEnabled( secret.global );
      setSelectedAgentIds( new Set( ( secret.agentMappings ?? [] ).map( m => m.agentId ) ) );
      setSelectAllInWorkspace( false );
      setConstellationFilter( '' );
      setServerError( null );
      reset( { envVarName: initialEnvVarName } );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isOpen, secret.id ] );

  // Clear server error when env var changes
  useEffect( () => {
    if ( serverError ) setServerError( null );
  }, [ watchedEnvVar ] );

  // --- Constellation data ---

  const constellationRows: ConstellationRow[] = useMemo( () => {
    if ( !agents ) return [];
    return agents.map( a => ( {
      id: a.id ?? '',
      name: a.name ?? a.id ?? ''
    } ) ).filter( a => a.id );
  }, [ agents ] );

  const filteredRows = useMemo( () => {
    if ( !constellationFilter.trim() ) return constellationRows;
    const query = constellationFilter.trim().toLowerCase();
    return constellationRows.filter( r => r.name.toLowerCase().includes( query ) );
  }, [ constellationRows, constellationFilter ] );

  // --- Selection helpers ---

  const toggleAgent = useCallback( ( agentId: string ) => {
    setSelectAllInWorkspace( false );
    setSelectedAgentIds( prev => {
      const next = new Set( prev );
      if ( next.has( agentId ) ) {
        next.delete( agentId );
      } else {
        next.add( agentId );
      }
      return next;
    } );
  }, [] );

  const selectAllOnPage = useCallback( ( pageAgentIds: string[], allSelected: boolean ) => {
    setSelectAllInWorkspace( false );
    setSelectedAgentIds( prev => {
      const next = new Set( prev );
      if ( allSelected ) {
        pageAgentIds.forEach( id => next.delete( id ) );
      } else {
        pageAgentIds.forEach( id => next.add( id ) );
      }
      return next;
    } );
  }, [] );

  const handleSelectAllWorkspace = useCallback( () => {
    setSelectAllInWorkspace( true );
    setSelectedAgentIds( new Set( constellationRows.map( r => r.id ) ) );
  }, [ constellationRows ] );

  // --- Table ---

  const columns = useMemo( () => [
    columnHelper.display( {
      id: 'checkbox',
      header: ( { table } ) => {
        const pageRows = table.getRowModel().rows;
        const pageIds = pageRows.map( r => r.original.id );
        const allPageSelected = pageIds.length > 0 && pageIds.every( id => selectedAgentIds.has( id ) );

        return (
          <span
            className={ styles.checkbox_header }
            onClick={ () => selectAllOnPage( pageIds, allPageSelected ) }
            role="button"
            tabIndex={ 0 }
            onKeyDown={ e => { if ( e.key === 'Enter' || e.key === ' ' ) selectAllOnPage( pageIds, allPageSelected ); } }
          >
            <Icon
              icon={ allPageSelected ? BiCheckCircle : BiCircle }
              size="sm"
              className={ clsx( styles.checkbox_icon, allPageSelected && styles.checkbox_icon_checked ) }
            />
          </span>
        );
      },
      cell: info => {
        const id = info.row.original.id;
        const isChecked = selectedAgentIds.has( id );

        return (
          <span
            onClick={ () => toggleAgent( id ) }
            role="button"
            tabIndex={ 0 }
            onKeyDown={ e => { if ( e.key === 'Enter' || e.key === ' ' ) toggleAgent( id ); } }
          >
            <Icon
              icon={ isChecked ? BiCheckCircle : BiCircle }
              size="sm"
              className={ clsx( styles.checkbox_icon, isChecked && styles.checkbox_icon_checked ) }
            />
          </span>
        );
      },
      size: 50
    } ),
    columnHelper.accessor( 'name', {
      header: () => (
        <span>
          Constellation
          <AiOutlineInfoCircle className={ styles.header_info_icon } />
        </span>
      ),
      cell: info => <span className={ styles.constellation_name }>{ info.getValue() }</span>,
      size: 500
    } )
  ], [ selectedAgentIds, toggleAgent, selectAllOnPage ] );

  const table = useNativeTable<ConstellationRow>( {
    data: filteredRows,
    columns,
    enableSorting: false,
    initialState: {
      pagination: { pageSize: DEFAULT_PAGE_SIZE }
    }
  } );

  // Select-all banner logic
  const pageRows = table.getRowModel().rows;
  const pageIds = pageRows.map( r => r.original.id );
  const allPageSelected = pageIds.length > 0 && pageIds.every( id => selectedAgentIds.has( id ) );
  const totalConstellations = constellationRows.length;
  const showSelectAllBanner = allPageSelected && !selectAllInWorkspace && totalConstellations > pageIds.length;

  // --- Submit ---

  const handleSubmit = useCallback( async() => {
    const envVarName = watchedEnvVar?.trim();
    if ( !envVarName ) return;

    setServerError( null );

    try {
      // Always update global flag
      await updateGlobalMutation.mutateAsync( {
        secretId: secret.id,
        body: {
          global: globalEnabled,
          globalEnvVarName: globalEnabled ? envVarName : undefined,
          userId
        }
      } );

      // If non-global, also update agent mappings
      if ( !globalEnabled && secretDetail ) {
        await updateMutation.mutateAsync( {
          secretId: secret.id,
          body: {
            value: secretDetail.value,
            userId,
            envVarName,
            agentIds: Array.from( selectedAgentIds )
          }
        } );
      }

      toast.success( 'Secret access updated' );
      onClose();
    } catch( error: unknown ) {
      const err = error as { message?: string; body?: { error?: { message?: string; collisions?: unknown[] } } };
      const errorMsg = err?.body?.error?.message ?? err?.message ?? '';

      if ( errorMsg.toLowerCase().includes( 'collision' ) || errorMsg.toLowerCase().includes( 'already' ) ) {
        setServerError( 'Variable name is already in use. Pick a new unique name.' );
      } else {
        toast.error( 'Failed to update secret access' );
      }
    }
  }, [ watchedEnvVar, globalEnabled, selectedAgentIds, secret.id, secretDetail, userId, updateGlobalMutation, updateMutation, onClose ] );

  // --- ESC key handler ---

  useEffect( () => {
    if ( !isOpen ) return;

    const handleKeyDown = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        onClose();
      }
    };

    document.addEventListener( 'keydown', handleKeyDown );
    return () => document.removeEventListener( 'keydown', handleKeyDown );
  }, [ isOpen, onClose ] );

  if ( !isOpen ) return null;

  // Wait for secretDetail before enabling save: handleSubmit needs secretDetail.value
  // to send a valid UpdateSecretReq for agent-mapping changes.
  const isFormDisabled = !!errors.envVarName || !watchedEnvVar?.trim() || !!serverError || isPending || !secretDetail;

  return (
    <Modal className={ styles.modal }>
      <div className={ styles.modal_content }>
        <div className={ styles.modal_header }>
          <Heading as="h2" size="md" className={ styles.title }>
            Constellations access to secret
            <GrNodes className={ styles.title_icon } />
          </Heading>
          <Text size="md" className={ styles.subtitle }>
            Choose which constellations can use { secret.name }
          </Text>
        </div>

        <div className={ styles.scrollable_content }>

          {/* Set Environment Variable */}
          <div className={ styles.section }>
            <h3 className={ clsx( styles.section_label, ( errors.envVarName || serverError ) && styles.field_label_error ) }>
              Set Environment Variable
            </h3>
            <div className={ styles.field_header }>
              <p className={ styles.section_helper } style={ { margin: 0 } }>
                Used to reference this secret in constellations.
              </p>
              { serverError ? (
                <span className={ styles.validation_error }>{ serverError }</span>
              ) : errors.envVarName ? (
                <span className={ styles.validation_error }>{ errors.envVarName.message }</span>
              ) : envVarCharCounter ? (
                <span className={ styles.char_counter }>{ envVarCharCounter }</span>
              ) : (
                <span className={ styles.field_helper }>
                  Uppercase letters (A&ndash;Z), numbers (0&ndash;9), and underscores (_) only. Max { MAX_ENV_VAR_LENGTH } characters
                </span>
              ) }
            </div>
            <div style={ { position: 'relative' } }>
              <input
                { ...register( 'envVarName' ) }
                type="text"
                maxLength={ MAX_ENV_VAR_LENGTH }
                className={ clsx(
                  styles.input_field,
                  { [styles.input_error]: errors.envVarName || serverError }
                ) }
                data-testid="env-var-name-input"
              />
              { serverError && <MdWarning className={ styles.warning_icon } style={ { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' } } /> }
            </div>
          </div>

          {/* Global Constellations Access */}
          <div className={ styles.section }>
            <h3 className={ styles.section_label }>Global constellations access</h3>
            <p className={ styles.section_helper }>
              Allow all constellations in this workspace to use this secret.
            </p>
            <div className={ styles.toggle_row }>
              <span className={ styles.toggle_label }>Global Access:</span>
              <span
                className={ styles.toggle_icon }
                onClick={ () => setGlobalEnabled( v => !v ) }
                role="button"
                tabIndex={ 0 }
                onKeyDown={ e => { if ( e.key === 'Enter' || e.key === ' ' ) setGlobalEnabled( v => !v ); } }
                data-testid="global-access-toggle"
              >
                <Icon icon={ globalEnabled ? BsToggleOn : BsToggleOff } />
              </span>
              <span>{ globalEnabled ? 'Enabled' : 'Disabled' }</span>
              <span className={ styles.toggle_status }>
                { globalEnabled
                  ? 'All constellations can access this secret.'
                  : 'Access is limited to selected constellations.'}
              </span>
            </div>
          </div>

          {/* Select Constellations (non-global only) */}
          { !globalEnabled && (
            <div className={ styles.section }>
              <div className={ styles.constellation_header }>
                <div>
                  <h3 className={ styles.section_label }>Select constellations with access</h3>
                  <p className={ styles.section_helper }>
                    Grant access to the constellations that require this secret.
                  </p>
                </div>
                <div className={ styles.filter_wrapper }>
                  <span className={ styles.filter_label }>Filter by constellation</span>
                  <input
                    type="text"
                    className={ styles.filter_input }
                    placeholder="Filter by constellation..."
                    value={ constellationFilter }
                    onChange={ e => setConstellationFilter( e.target.value ) }
                    data-testid="constellation-filter"
                  />
                </div>
              </div>

              { showSelectAllBanner && (
                <div className={ styles.select_all_banner } data-testid="select-all-banner">
                  All { pageIds.length } constellations on this page are selected.{' '}
                  <span
                    className={ styles.select_all_link }
                    onClick={ handleSelectAllWorkspace }
                    role="button"
                    tabIndex={ 0 }
                    onKeyDown={ e => { if ( e.key === 'Enter' || e.key === ' ' ) handleSelectAllWorkspace(); } }
                  >
                    Select all { totalConstellations } constellations in workspace.
                  </span>
                </div>
              ) }

              <NativeTable
                table={ table }
                pagination="client"
                tbodyTestId="table-body-constellations"
                rowClassName={ row => ( selectedAgentIds.has( row.original.id ) ? styles.row_selected : undefined ) }
              />
            </div>
          ) }
        </div>

        {/* Footer */}
        <div className={ styles.modal_footer }>
          <div className={ styles.footer_actions }>
            <Button variant="secondary" size="sm" onClick={ onClose } type="button">
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={ handleSubmit }
              disabled={ isFormDisabled }
              loading={ isPending }
              data-testid="grant-access-button"
            >
              Grant Access
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
